import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { authApi } from '../api/authApi';
import { userApi } from '../api/services/userApi';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [orgType, setOrgType] = useState(null);

  // Token validation function
  const isTokenValid = (token) => {
    try {
      // Decode JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      // If token is malformed, consider it invalid
      return false;
    }
  };

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = authApi.getToken();
      const storedUser = authApi.getCurrentUser();
      
      if (storedToken && storedUser) {
        // Validate token by checking if it's expired
        if (isTokenValid(storedToken)) {
          setToken(storedToken);
          setUser(storedUser);
          
          // Extract orgType from token if not already in state
          try {
            const payload = JSON.parse(atob(storedToken.split('.')[1]));
            if (payload.orgType) {
              setOrgType(payload.orgType);
              localStorage.setItem('organizationType', payload.orgType);
            }
          } catch (e) {}

          // Silently refresh user data from backend
          userApi.getMe().then(result => {
            if (result.success) {
              setUser(result.data);
              localStorage.setItem('user', JSON.stringify(result.data));
            }
          }).catch(() => {
            // Silently ignore errors during background refresh
          });
        } else {
          // Token is expired, clear it
          authApi.logout();
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Auto-logout on token expiry
  useEffect(() => {
    if (token) {
      const checkTokenExpiry = () => {
        if (!isTokenValid(token)) {
          logout();
          toast.error('Session expired. Please login again.');
        }
      };

      // Check token every 5 minutes
      const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [token]);

  const login = async (loginData) => {
    try {
      setLoading(true);
      const result = await authApi.login(loginData);
      
      if (result.success) {
        setToken(result.data.access_token);
        setUser(result.data.user);
        if (result.data.organization?.orgType) {
          setOrgType(result.data.organization.orgType);
        }
        toast.success('Login successful!');
        return { success: true };
      } else {
        toast.error(result.message || 'Login failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      // Use structured error message from backend
      const message = error.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const result = await authApi.register(userData);
      
      if (result.success) {
        setToken(result.data.access_token);
        setUser(result.data.user);
        if (result.data.organization?.orgType) {
          setOrgType(result.data.organization.orgType);
          localStorage.setItem('organizationType', result.data.organization.orgType);
        }
        toast.success('Registration successful!');
        return { success: true };
      } else {
        toast.error(result.message || 'Registration failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      // Use structured error message from backend
      const message = error.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      setToken(null);
      setUser(null);
      setOrgType(null);
      toast.success('Logged out successfully');
    } catch (error) {
      // Even if logout API fails, clear local state
      setToken(null);
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const result = await userApi.updateMe(profileData);
      if (result.success) {
        setUser(result.data);
        localStorage.setItem('user', JSON.stringify(result.data));
        toast.success('Profile updated successfully!');
        return { success: true };
      } else {
        toast.error(result.message || 'Profile update failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = error.message || 'Profile update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const refreshUser = async () => {
    try {
      const result = await userApi.getMe();
      if (result.success) {
        setUser(result.data);
        localStorage.setItem('user', JSON.stringify(result.data));
        return { success: true, data: result.data };
      }
      return { success: false, message: result.message };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const result = await userApi.changePassword({ currentPassword, newPassword });
      if (result.success) {
        toast.success('Password changed successfully!');
        return { success: true };
      } else {
        toast.error(result.message || 'Password change failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = error.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const value = {
    user,
    loading,
    token,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    orgType: orgType || localStorage.getItem('organizationType'),
    isAuthenticated: !!token && !!user,
    isAdmin: user?.userRole === 'admin',
    isTeacher: user?.userRole === 'teacher',
    refreshToken: async () => {
      // This would call a refresh token endpoint
      // For now, we'll just validate the current token
      if (token && isTokenValid(token)) {
        return { success: true };
      } else {
        logout();
        return { success: false };
      }
    },
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
